import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import { useLocale } from '@hooks/useLocale';
import { MecProduct } from '@adapters/bigcommerce/types/product';
import useProduct from '@adapters/bigcommerce/hooks/product/useProduct';

const COMPARISON_ITEMS_KEY = 'comparison_items';

export interface State {
  pending: Array<string>;
  items: Array<MecProduct>;
  allItems: Array<string>;
}

export interface Actions {
  addItem: (item: MecProduct) => void;
  removeItem: (code?: string) => void;
  clearItems: () => void;
  addPendingItem: (code?: string) => void;
  isItemInComparison: (code: string) => boolean;
}

export type ComparisonState = State & Actions;

enum ActionType {
  LOAD_COMPARISON_ITEMS = 'LOAD_COMPARISON_ITEMS',
  ADD_COMPARISON_ITEM = 'ADD_COMPARISON_ITEM',
  REMOVE_COMPARISON_ITEM = 'REMOVE_COMPARISON_ITEM',
  CLEAR_COMPARISON_ITEMS = 'CLEAR_COMPARISON_ITEMS',
  ADD_PENDING_ITEM = 'ADD_PENDING_ITEM',
  UPDATE_PENDING_ITEM = 'UPDATE_PENDING_ITEM',
  REMOVE_PENDING_ITEM = 'REMOVE_PENDING_ITEM',
}

type Action =
  | { type: ActionType.ADD_COMPARISON_ITEM; item: MecProduct }
  | { type: ActionType.REMOVE_COMPARISON_ITEM; code?: string }
  | { type: ActionType.CLEAR_COMPARISON_ITEMS }
  | { type: ActionType.LOAD_COMPARISON_ITEMS; codes: string[] }
  | { type: ActionType.ADD_PENDING_ITEM; code: string }
  | { type: ActionType.UPDATE_PENDING_ITEM; item: MecProduct }
  | { type: ActionType.REMOVE_PENDING_ITEM; code: string };

const initialState: ComparisonState = {
  items: [],
  pending: [],
  allItems: [],
  addItem: () => null,
  removeItem: () => null,
  clearItems: () => null,
  addPendingItem: () => null,
  isItemInComparison: () => false,
};

export const ComparisonContext = createContext<ComparisonState>(initialState);

ComparisonContext.displayName = 'ComparisonContext';

const comparisonReducer = (state: State, action: Action) => {
  const setItems = (items: Array<MecProduct>) => {
    localStorage.setItem(
      COMPARISON_ITEMS_KEY,
      JSON.stringify(items.map((item) => item.code))
    );
  };

  switch (action.type) {
    case ActionType.ADD_COMPARISON_ITEM: {
      if (!action.item.code) {
        return state;
      }
      if (state.items.some((item) => item.code === action.item.code)) {
        return state;
      }

      const newItems = [...state.items, action.item];

      setItems(newItems);

      return {
        ...state,
        items: newItems,
      };
    }
    case ActionType.REMOVE_COMPARISON_ITEM: {
      if (!action.code) {
        return state;
      }
      const newItems = state.items.filter((item) => item.code !== action.code);

      setItems(newItems);

      return {
        ...state,
        items: newItems,
        pending: state.pending.filter((item) => item !== action.code),
      };
    }
    case ActionType.CLEAR_COMPARISON_ITEMS: {
      localStorage.removeItem(COMPARISON_ITEMS_KEY);
      return {
        ...state,
        items: [],
        pending: [],
      };
    }
    case ActionType.ADD_PENDING_ITEM: {
      if (state.pending.includes(action.code)) {
        return state;
      }
      return {
        ...state,
        pending: state.pending.concat(action.code),
      };
    }
    case ActionType.REMOVE_PENDING_ITEM: {
      return {
        ...state,
        pending: state.pending.filter((item) => item !== action.code),
      };
    }
    case ActionType.UPDATE_PENDING_ITEM: {
      // If item is not pending, it can't be updated
      if (!state.pending.some((item) => item === action.item.code)) {
        return state;
      }

      const items = state.items;
      let newItems: MecProduct[];

      if (items.some((product) => product.code === action.item.code)) {
        // Update existing item
        newItems = items.map((item) =>
          item.code === action.item.code ? action.item : item
        );
      } else {
        // Add new item
        newItems = items.concat(action.item);
      }

      setItems(newItems);

      return {
        ...state,
        items: newItems,
        pending: state.pending.filter((item) => item !== action.item.code),
      };
    }

    case ActionType.LOAD_COMPARISON_ITEMS: {
      return {
        ...state,
        pending: action.codes,
      };
    }
    default: {
      return state;
    }
  }
};

export interface ComparisonContextProviderProps {
  children: ReactNode;
}

export const ComparisonContextProvider = ({
  children,
}: ComparisonContextProviderProps) => {
  const locale = useLocale();
  const { getProduct } = useProduct(); // loads all the product data which likely takes a while, possibly set up new endpoint with reduced data load
  const [state, dispatch] = useReducer(comparisonReducer, initialState);

  const addItem = (item: MecProduct) =>
    dispatch({ type: ActionType.ADD_COMPARISON_ITEM, item });

  const removeItem = (code?: string) => {
    const removeItemEvent = new CustomEvent('removedCompareItems', {
      detail: [code],
    });
    window.dispatchEvent(removeItemEvent);
    dispatch({ type: ActionType.REMOVE_COMPARISON_ITEM, code });
  };

  const loadFromLocalStorage = () => {
    const stored = localStorage.getItem(COMPARISON_ITEMS_KEY);
    if (stored) {
      const codes = JSON.parse(stored);
      if (codes.length) {
        dispatch({ type: ActionType.LOAD_COMPARISON_ITEMS, codes });
      }
    }
  };

  useEffect(() => {
    if (!state.items.length) {
      loadFromLocalStorage();
    }
  }, [state.items]);

  useEffect(() => {
    window.addEventListener('storage', loadFromLocalStorage);

    return () => {
      window.removeEventListener('storage', loadFromLocalStorage);
    };
  }, []);

  const allItems = useMemo(() => {
    return (
      state.items
        .map((product) => product.code ?? '')
        .concat(state.pending)
        // Remove any possible duplicates
        .reduce((aggregate: string[], next: string) => {
          if (!aggregate.includes(next)) {
            aggregate.push(next);
          }
          return aggregate;
        }, [])
    );
  }, [state.items, state.pending]);

  const clearItems = useCallback(() => {
    const removeItemEvent = new CustomEvent('removedCompareItems', {
      detail: allItems,
    });
    window.dispatchEvent(removeItemEvent);
    dispatch({ type: ActionType.CLEAR_COMPARISON_ITEMS });
  }, [allItems]);

  const isItemInComparison = useCallback(
    (code: string) => !!allItems.find((itemCode) => itemCode === code),
    [allItems]
  );

  const addPendingItem = useCallback(
    async (code?: string) => {
      if (!code || code.toString() === '1') {
        return;
      }

      dispatch({ type: ActionType.ADD_PENDING_ITEM, code });

      try {
        const product = await getProduct(code);

        if (product) {
          dispatch({ type: ActionType.UPDATE_PENDING_ITEM, item: product });
        } else {
          dispatch({ type: ActionType.REMOVE_PENDING_ITEM, code });
        }
      } catch {
        dispatch({ type: ActionType.REMOVE_PENDING_ITEM, code });
      }
    },
    [getProduct]
  );

  const loadComparisonItems = useCallback(
    (codes: string[]) => {
      const uniqueCodes = [...new Set(codes)];
      dispatch({ type: ActionType.LOAD_COMPARISON_ITEMS, codes: uniqueCodes });

      uniqueCodes.forEach(async (code) => {
        try {
          const product = await getProduct(code);

          if (product) {
            dispatch({ type: ActionType.UPDATE_PENDING_ITEM, item: product });
          } else {
            dispatch({ type: ActionType.REMOVE_PENDING_ITEM, code });
          }
        } catch (error) {
          dispatch({ type: ActionType.REMOVE_PENDING_ITEM, code });
        }
      });
    },
    [getProduct]
  );

  useEffect(() => {
    const stored = localStorage.getItem(COMPARISON_ITEMS_KEY);

    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed?.length) {
        loadComparisonItems(parsed);
      }
    }
  }, [loadComparisonItems, locale]);

  useEffect(() => {
    const updateItemEvent = new CustomEvent('currentCompareItems', {
      detail: allItems,
    });
    window.dispatchEvent(updateItemEvent);
  }, [allItems]);

  const value = useMemo(
    () => ({
      ...state,
      addItem,
      removeItem,
      clearItems,
      allItems,
      isItemInComparison,
      addPendingItem,
    }),
    [addPendingItem, allItems, clearItems, isItemInComparison, state]
  );

  return (
    <ComparisonContext.Provider value={value}>
      {children}
    </ComparisonContext.Provider>
  );
};

export const useComparison = () => {
  const context = useContext(ComparisonContext);
  if (context === undefined) {
    throw new Error(`useComparison must be used within a ComparisonProvider`);
  }
  return context;
};
